import { bluetokaiProfile } from "./bluetokai";
import { defaultProfile } from "./defaultProfile";
import { dotpeProfile } from "./dotpe";
import { xenoProfile } from "./xeno";
import type { LoyaltyProviderProfile } from "./types";

/**
 * metaId → capability profile.
 *
 * Only providers that DIFFER from the baseline appear here. `reelo`, `mloyal` and every provider a
 * merchant enables in future resolve to `defaultProfile` — see `getProviderProfile`.
 *
 * ⚠️ **`xeno` was deliberately absent between 2026-09-08 and 2026-09-18**, its file deleted,
 * because once the coupon flags moved to `packages/core/promotions/partners` what was left was
 * byte-identical to the baseline — dead weight by the suite's own "only registers providers that
 * actually differ" rule. It is back with exactly one delta: `supportsEnrollmentConsent`, which
 * only it and dotpe have on the backend. Its coupon behaviour stays in `promotions/partners`.
 */
export const LOYALTY_PROVIDER_PROFILES: Record<string, LoyaltyProviderProfile> = {
  [dotpeProfile.metaId]: dotpeProfile,
  [xenoProfile.metaId]: xenoProfile,
  [bluetokaiProfile.metaId]: bluetokaiProfile,
};

/**
 * The capability profile for a provider.
 *
 * 🔴 **Never throws and never returns undefined.** An unknown, absent or newly-enabled `metaId`
 * answers `defaultProfile`, so a provider nobody has written a file for still gets a working,
 * conservative flow instead of a blank panel or a crash. That is the property that makes this
 * registry safe to consult from every rule.
 */
export const getProviderProfile = (metaId?: string): LoyaltyProviderProfile =>
  (metaId && LOYALTY_PROVIDER_PROFILES[metaId]) || defaultProfile;

/** Every profile that has been written, for tests and tooling. Excludes the baseline. */
export const registeredProfiles = (): LoyaltyProviderProfile[] =>
  Object.values(LOYALTY_PROVIDER_PROFILES);
